import type {
  GeneratedStack,
  GeneratedNode,
  GeneratedEdge,
  GeneratedGroup,
} from '../types';
import { POPULAR_ICONS } from '../constants';
import { getFallbackIconForCategory } from './iconValidation';

/**
 * Hand-rolled parser for the Mermaid subsets that LLMs actually emit:
 * flowcharts (graph/flowchart, node shapes, -->, subgraphs) and class
 * diagrams (classDiagram — class blocks, members, relations). Produces the
 * same `GeneratedStack` IR as AI generation, so validation, materialization
 * and layout downstream are shared. Unknown lines are skipped with a
 * warning; other Mermaid diagram types fail with a clear "not supported"
 * error.
 */

export class MermaidParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MermaidParseError';
  }
}

export type MermaidDirection = 'TB' | 'BT' | 'LR' | 'RL';

export interface MermaidParseResult {
  stack: GeneratedStack;
  direction: MermaidDirection;
  warnings: string[];
}

/** Diagram types we deliberately reject with a helpful message. */
const UNSUPPORTED_TYPES: Record<string, string> = {
  sequencediagram: 'sequence diagram',
  erdiagram: 'ER diagram',
  statediagram: 'state diagram',
  'statediagram-v2': 'state diagram',
  gantt: 'Gantt chart',
  pie: 'pie chart',
  journey: 'user journey',
  gitgraph: 'git graph',
  mindmap: 'mindmap',
  timeline: 'timeline',
  c4context: 'C4 diagram',
  c4container: 'C4 diagram',
  c4component: 'C4 diagram',
  c4deployment: 'C4 diagram',
  quadrantchart: 'quadrant chart',
  requirementdiagram: 'requirement diagram',
  sankey: 'sankey diagram',
  'sankey-beta': 'sankey diagram',
  xychart: 'XY chart',
  'xychart-beta': 'XY chart',
  'block-beta': 'block diagram',
  'packet-beta': 'packet diagram',
  kanban: 'kanban board',
  'architecture-beta': 'architecture diagram',
};

/** Statements that carry styling/metadata we intentionally drop. */
const SKIPPED_PREFIXES = [
  'linkstyle',
  'classdef',
  'class ',
  'style ',
  'click ',
  'accdescr',
  'acctitle',
];

interface ParsedNode {
  id: string;
  label: string;
  groupId?: string;
}

export function parseMermaidToStack(code: string): MermaidParseResult {
  const warnings: string[] = [];

  if (!code || !code.trim()) {
    throw new MermaidParseError('Paste some Mermaid code first.');
  }

  const statements = toStatements(code);
  if (statements.length === 0) {
    throw new MermaidParseError("That doesn't look like Mermaid code.");
  }

  // --- Header ---
  const header = statements[0];

  // Class diagrams take a different body grammar — dispatch before the
  // flowchart header check.
  if (/^classdiagram(-v2)?$/i.test(header.replace(/\s+/g, ''))) {
    return parseClassDiagram(statements.slice(1), warnings);
  }

  const headerMatch = /^(graph|flowchart)(?:\s+(TD|TB|BT|LR|RL))?$/i.exec(header);
  if (!headerMatch) {
    const key = header.toLowerCase().replace(/[_\s]+/g, '');
    const unsupported = UNSUPPORTED_TYPES[key];
    if (unsupported) {
      throw new MermaidParseError(
        `Mermaid ${unsupported}s aren't supported yet — paste a flowchart ("graph TD") or a class diagram ("classDiagram").`
      );
    }
    if (/^(graph|flowchart)\b/i.test(header)) {
      throw new MermaidParseError(
        `Couldn't understand the flowchart header "${header}" — expected something like "graph TD" or "flowchart LR".`
      );
    }
    throw new MermaidParseError(
      'This doesn\'t look like supported Mermaid code — it should start with "graph TD", "flowchart LR" or "classDiagram".'
    );
  }

  const rawDir = (headerMatch[2] || 'TD').toUpperCase();
  const direction: MermaidDirection = rawDir === 'TD' ? 'TB' : (rawDir as MermaidDirection);

  // --- Body ---
  const nodes = new Map<string, ParsedNode>();
  const edges: GeneratedEdge[] = [];
  const groups: GeneratedGroup[] = [];
  const groupById = new Map<string, GeneratedGroup>();
  const groupStack: string[] = [];

  const currentGroupId = () =>
    groupStack.length > 0 ? groupStack[groupStack.length - 1] : undefined;

  const ensureNode = (ref: string, quoted: string[]): string | null => {
    const parsed = parseNodeRef(ref, quoted);
    if (!parsed) {
      warnings.push(`Skipped unrecognized node "${ref.trim()}".`);
      return null;
    }
    if (groupById.has(parsed.id)) {
      // Edges to a whole subgraph aren't supported; point at nothing.
      warnings.push(`Edges to the subgraph "${parsed.id}" aren't supported — that edge was skipped.`);
      return null;
    }
    const existing = nodes.get(parsed.id);
    if (existing) {
      // A later explicit label wins over the bare-id placeholder.
      if (parsed.label && existing.label === existing.id) existing.label = parsed.label;
      return existing.id;
    }
    const groupId = currentGroupId();
    nodes.set(parsed.id, { id: parsed.id, label: parsed.label || parsed.id, groupId });
    if (groupId) groupById.get(groupId)?.nodeIds.push(parsed.id);
    return parsed.id;
  };

  for (const rawStmt of statements.slice(1)) {
    // Mask quoted spans first so arrows/semicolons inside labels ("a --> b")
    // aren't tokenized as syntax. Labels are unmasked when they're finalized.
    const { masked: stmt, quoted } = maskQuotes(rawStmt);
    const lower = stmt.toLowerCase();

    if (/^subgraph\b/i.test(stmt)) {
      const group = parseSubgraph(stmt, quoted);
      if (groupStack.length > 0) {
        warnings.push('Nested subgraphs are imported as flat groups.');
      }
      // Two subgraphs can slugify to the same id — keep ids unique.
      let uniqueId = group.id;
      for (let n = 2; groupById.has(uniqueId); n++) uniqueId = `${group.id}_${n}`;
      group.id = uniqueId;
      groups.push(group);
      groupById.set(group.id, group);
      groupStack.push(group.id);
      continue;
    }

    if (lower === 'end') {
      if (groupStack.length === 0) {
        warnings.push('Ignored an "end" without a matching subgraph.');
      } else {
        groupStack.pop();
      }
      continue;
    }

    if (lower.startsWith('direction ')) {
      warnings.push('Per-subgraph "direction" statements are ignored — the layout follows the main header.');
      continue;
    }

    if (SKIPPED_PREFIXES.some((p) => lower.startsWith(p))) {
      warnings.push('Mermaid styling/metadata lines were skipped — the diagram is re-styled with StackSketch\'s palette.');
      continue;
    }

    if (parseEdgeStatement(stmt, (ref) => ensureNode(ref, quoted), edges, quoted)) continue;

    // Plain node declaration (e.g. `db[(PostgreSQL)]` on its own line).
    if (parseNodeRef(stmt, quoted)) {
      ensureNode(stmt, quoted);
      continue;
    }

    warnings.push(`Skipped unrecognized line "${truncate(rawStmt)}".`);
  }

  if (groupStack.length > 0) {
    warnings.push('A subgraph was never closed with "end" — it was closed automatically.');
  }

  if (nodes.size === 0) {
    throw new MermaidParseError(
      'No nodes found. Paste flowchart code like "graph TD" followed by lines such as "A[Web] --> B[API]".'
    );
  }

  const stackNodes: GeneratedNode[] = [...nodes.values()].map((n) => {
    const guess = guessIcon(n.label);
    return {
      id: n.id,
      label: n.label,
      icon: guess.icon,
      description: '',
      category: guess.category,
      groupId: n.groupId,
    };
  });

  const stack: GeneratedStack = {
    nodes: stackNodes,
    edges,
    groups: groups.filter((g) => g.nodeIds.length > 0),
  };

  return { stack, direction, warnings };
}

// --- Class diagrams -------------------------------------------------------------

interface ParsedClass {
  id: string;
  label: string;
  members: string[];
  annotations: string[];
}

/** Class name, optionally with Mermaid generics: `List~Item~`. */
const CLASS_ID_SRC = String.raw`[\p{L}\p{N}_]+(?:~[^~\s]+~)?`;
/** A quoted span masked by maskQuotes — cardinality labels like "1" / "many". */
const CARD_SRC = String.raw`(?:\u0000\d+\u0000\s*)?`;

/**
 * Relation operators, longest first: `<|--` inheritance, `..|>` realization,
 * `*--` composition, `o--` aggregation, `-->` association, `..>` dependency,
 * their reversed forms, and the plain `--` / `..` links. Cardinality quotes
 * (`A "1" *-- "many" B`) arrive masked and are skipped.
 */
const CLASS_LINK_RE = new RegExp(
  `^${CARD_SRC}(${CLASS_ID_SRC})\\s*${CARD_SRC}` +
    `(<\\|\\.\\.|\\.\\.\\|>|<\\|--|--\\|>|\\*--|--\\*|o--|--o|<\\.\\.|\\.\\.>|<--|-->|\\.\\.|--)` +
    `\\s*${CARD_SRC}(${CLASS_ID_SRC})${CARD_SRC}\\s*(?::\\s*(.+))?$`,
  'u'
);

/** `List~Item~` → id `List`, label `List<Item>` (the id is what relations reference). */
function splitClassRef(ref: string): { id: string; label: string } {
  const m = /^([\p{L}\p{N}_]+)(?:~([^~\s]+)~)?$/u.exec(ref.trim());
  if (!m) return { id: ref.trim(), label: ref.trim() };
  return { id: m[1], label: m[2] ? `${m[1]}<${m[2]}>` : m[1] };
}

/** Members keep their visibility marker; `~T~` generics render as `<T>`. */
function cleanMember(raw: string, quoted: string[]): string {
  const unmasked = raw.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => quoted[Number(i)] ?? '');
  return unmasked
    .replace(/~([^~\s]+)~/g, '<$1>')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Maps a Mermaid class relation to a GeneratedEdge. Arrowheads land where
 * UML puts them: inheritance/realization point at the parent (`A <|-- B`
 * reads "B extends A"), composition/aggregation flow from the container.
 * An explicit `: label` always wins over the default wording.
 */
function classRelation(
  op: string,
  left: string,
  right: string,
  label?: string
): GeneratedEdge {
  const edge = (source: string, target: string, def?: string, type?: GeneratedEdge['type']) => {
    const e: GeneratedEdge = { source, target };
    if (label ?? def) e.label = label ?? def;
    if (type) e.type = type;
    return e;
  };
  switch (op) {
    case '<|--': return edge(right, left, 'extends');
    case '--|>': return edge(left, right, 'extends');
    case '<|..': return edge(right, left, 'implements', 'dependency');
    case '..|>': return edge(left, right, 'implements', 'dependency');
    case '*--': return edge(left, right, 'contains');
    case '--*': return edge(right, left, 'contains');
    case 'o--': return edge(left, right, 'has');
    case '--o': return edge(right, left, 'has');
    case '<--': return edge(right, left);
    case '-->': return edge(left, right);
    case '<..': return edge(right, left, undefined, 'dependency');
    case '..>': return edge(left, right, undefined, 'dependency');
    case '..': return edge(left, right, undefined, 'dependency');
    default: return edge(left, right); // '--'
  }
}

/**
 * Node descriptions render as a single short line, so class members can't be
 * listed verbatim. Use the stereotype when there is one, otherwise a compact
 * member count ("3 fields · 2 methods").
 */
function classDescription(c: ParsedClass): string {
  if (c.annotations.length > 0) return c.annotations.join(' ');
  if (c.members.length === 0) return '';
  const methods = c.members.filter((m) => m.includes('(')).length;
  const fields = c.members.length - methods;
  const parts: string[] = [];
  if (fields > 0) parts.push(`${fields} field${fields === 1 ? '' : 's'}`);
  if (methods > 0) parts.push(`${methods} method${methods === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * Parses the body of a `classDiagram`. Classes become nodes with a short
 * single-line `description` (stereotype or member count — see
 * `classDescription`); relations become edges. Mermaid has no class-diagram
 * header direction, but a `direction` statement is honored (default TB).
 */
function parseClassDiagram(body: string[], warnings: string[]): MermaidParseResult {
  const classes = new Map<string, ParsedClass>();
  const edges: GeneratedEdge[] = [];
  let direction: MermaidDirection = 'TB';
  let openNamespaces = 0;

  const ensureClass = (ref: string): ParsedClass => {
    const { id, label } = splitClassRef(ref);
    const existing = classes.get(id);
    if (existing) {
      // A later generic form upgrades a bare-id label ("List" → "List<Item>").
      if (existing.label === existing.id && label !== id) existing.label = label;
      return existing;
    }
    const cls: ParsedClass = { id, label, members: [], annotations: [] };
    classes.set(id, cls);
    return cls;
  };

  const addMemberText = (cls: ParsedClass, text: string, quoted: string[]) => {
    const member = cleanMember(text, quoted);
    if (!member) return;
    const ann = /^<<(.+)>>$/.exec(member);
    if (ann) cls.annotations.push(`<<${ann[1]}>>`);
    else cls.members.push(member);
  };

  for (let i = 0; i < body.length; i++) {
    const { masked: stmt, quoted } = maskQuotes(body[i]);

    // class Foo { ... } — the block may span several statements or close on
    // the same line. Style bindings (`class Foo ::: hot`) are dropped.
    const classDecl = /^class\s+(.+)$/i.exec(stmt);
    if (classDecl) {
      const rest = classDecl[1].replace(/:::\s*[\p{L}\p{N}_-]+/gu, '').trim();
      const braceIdx = rest.indexOf('{');
      if (braceIdx === -1) {
        ensureClass(rest);
        continue;
      }
      const cls = ensureClass(rest.slice(0, braceIdx).trim());
      let closed = false;
      const inner = rest.slice(braceIdx + 1);
      const closeIdx = inner.indexOf('}');
      if (closeIdx !== -1) {
        addMemberText(cls, inner.slice(0, closeIdx), quoted);
        closed = true;
      } else if (inner.trim()) {
        addMemberText(cls, inner, quoted);
      }
      while (!closed && i + 1 < body.length) {
        i++;
        const memberStmt = maskQuotes(body[i]);
        const end = memberStmt.masked.indexOf('}');
        if (end !== -1) {
          addMemberText(cls, memberStmt.masked.slice(0, end), memberStmt.quoted);
          closed = true;
        } else {
          addMemberText(cls, memberStmt.masked, memberStmt.quoted);
        }
      }
      if (!closed) {
        warnings.push(`The class "${cls.id}" was never closed with "}" — it was closed automatically.`);
      }
      continue;
    }

    // A stray closing brace ends a namespace (warned about below) or is noise.
    if (stmt === '}') {
      if (openNamespaces > 0) openNamespaces--;
      else warnings.push('Ignored a "}" without a matching block.');
      continue;
    }

    // Relations: `A --> B`, `A <|-- B`, `A "1" *-- "many" B : has`.
    const rel = CLASS_LINK_RE.exec(stmt);
    if (rel) {
      const left = ensureClass(rel[1]);
      const right = ensureClass(rel[3]);
      const label = rel[4] ? finishLabel(rel[4], quoted) || undefined : undefined;
      edges.push(classRelation(rel[2], left.id, right.id, label));
      continue;
    }

    // Stereotype annotations: `<<interface>> Foo`.
    const annMatch = /^<<(.+?)>>\s*(?:class\s+)?(.+)$/i.exec(stmt);
    if (annMatch) {
      ensureClass(annMatch[2]).annotations.push(`<<${annMatch[1]}>>`);
      continue;
    }

    // Member/annotation via colon syntax: `Foo : +int id`.
    const memberMatch = new RegExp(`^(${CLASS_ID_SRC})\\s*:\\s*(.+)$`, 'u').exec(stmt);
    if (memberMatch) {
      addMemberText(ensureClass(memberMatch[1]), memberMatch[2], quoted);
      continue;
    }

    const dirMatch = /^direction\s+(TB|TD|BT|LR|RL)$/i.exec(stmt);
    if (dirMatch) {
      const d = dirMatch[1].toUpperCase();
      direction = d === 'TD' ? 'TB' : (d as MermaidDirection);
      continue;
    }

    if (/^note\b/i.test(stmt)) {
      warnings.push('Mermaid notes were skipped — add them as sticky notes on the canvas.');
      continue;
    }

    if (/^namespace\b/i.test(stmt)) {
      warnings.push('Namespaces are flattened — their classes are imported at the top level.');
      if (stmt.includes('{') && !stmt.includes('}')) openNamespaces++;
      continue;
    }

    warnings.push(`Skipped unrecognized line "${truncate(body[i])}".`);
  }

  if (classes.size === 0) {
    throw new MermaidParseError(
      'No classes found. Paste class-diagram code like "classDiagram" followed by lines such as "class Item { +int id }".'
    );
  }

  const stackNodes: GeneratedNode[] = [...classes.values()].map((c) => {
    const guess = guessIcon(c.label);
    return {
      id: c.id,
      label: c.label,
      icon: guess.icon,
      description: classDescription(c),
      category: guess.category,
    };
  });

  return { stack: { nodes: stackNodes, edges }, direction, warnings };
}

// --- Statement splitting -----------------------------------------------------

/** Normalize line endings, strip comments, split on `;` — respecting quotes. */
function toStatements(code: string): string[] {
  const text = code.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const out: string[] = [];

  for (const rawLine of text.split('\n')) {
    let current = '';
    let inQuote = false;
    for (let i = 0; i < rawLine.length; i++) {
      const ch = rawLine[i];
      const next = rawLine[i + 1];
      if (ch === '"') inQuote = !inQuote;
      if (!inQuote && ch === '%' && next === '%') break; // comment to end of line
      if (!inQuote && ch === ';') {
        if (current.trim()) out.push(current.trim());
        current = '';
        continue;
      }
      current += ch;
    }
    if (current.trim()) out.push(current.trim());
  }

  return out;
}

// --- Node refs -----------------------------------------------------------------

const NODE_ID_RE = /^([\p{L}\p{N}_]+)/u;

/** Bracket pairs, longest/most-specific first so `((..))` wins over `(..)`. */
const SHAPE_PAIRS: Array<[string, string]> = [
  ['(((', ')))'],
  ['((', '))'],
  ['([', '])'],
  ['[(', ')]'],
  ['[[', ']]'],
  ['{{', '}}'],
  ['[/', '/]'],
  ['[\\', '\\]'],
  ['[/', '\\]'],
  ['[\\', '/]'],
  ['>', ']'],
  ['[', ']'],
  ['(', ')'],
  ['{', '}'],
];

function parseNodeRef(ref: string, quoted: string[]): { id: string; label?: string } | null {
  const trimmed = ref.trim();
  const m = NODE_ID_RE.exec(trimmed);
  if (!m) return null;
  const id = m[1];
  const rest = trimmed.slice(id.length).trim();
  if (!rest) return { id };
  const rawLabel = extractBracketLabel(rest);
  if (rawLabel === null) return null;
  return { id, label: finishLabel(rawLabel, quoted) || id };
}

/** Returns the raw (still quote-masked) text between a matched bracket pair. */
function extractBracketLabel(rest: string): string | null {
  for (const [open, close] of SHAPE_PAIRS) {
    if (rest.startsWith(open) && rest.endsWith(close)) {
      return rest.slice(open.length, rest.length - close.length);
    }
  }
  return null;
}

/**
 * Quoted spans are replaced with placeholders before tokenizing so syntax
 * characters inside labels (arrows, pipes, semicolons) don't parse as edges.
 */
function maskQuotes(s: string): { masked: string; quoted: string[] } {
  const quoted: string[] = [];
  const masked = s.replace(/"([^"]*)"/g, (_match, inner: string) => {
    quoted.push(inner);
    return `\u0000${quoted.length - 1}\u0000`;
  });
  return { masked, quoted };
}

/** Restore masked quoted spans and normalize the final label text. */
function finishLabel(raw: string, quoted: string[]): string {
  const unmasked = raw.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => quoted[Number(i)] ?? '');
  return cleanLabel(unmasked);
}

function cleanLabel(raw: string): string {
  let s = raw.trim();
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    s = s.slice(1, -1);
  }
  return s
    .replace(/#quot;/g, '"')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// --- Subgraphs ------------------------------------------------------------------

function parseSubgraph(stmt: string, quoted: string[]): GeneratedGroup {
  const rest = stmt.replace(/^subgraph\s+/i, '').trim();
  const idMatch = NODE_ID_RE.exec(rest);

  if (idMatch) {
    const id = idMatch[1];
    const remainder = rest.slice(id.length).trim();
    // `subgraph plainId`
    if (!remainder) {
      return { id, label: id, nodeIds: [] };
    }
    // `subgraph id[Label]` / `subgraph id ["Label"]` (also (, {, > shapes)
    if (/^[[({>]/.test(remainder)) {
      const rawLabel = extractBracketLabel(remainder);
      const label = rawLabel !== null ? finishLabel(rawLabel, quoted) : finishLabel(remainder, quoted);
      return { id, label: label || id, nodeIds: [] };
    }
  }

  // `subgraph "Label Only"` or `subgraph Label With Spaces` — no explicit id.
  const label = finishLabel(rest, quoted) || rest;
  return { id: slugify(label), label, nodeIds: [] };
}

function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'group';
}

// --- Edges ------------------------------------------------------------------------

/**
 * Link operators, longest first. Optional pipe label captured after the arrow
 * (`A -->|reads| B`). Inline label forms (`A -- reads --> B`) are rewritten to
 * pipe form before tokenizing.
 */
const LINK_RE = /(<-->|-.->|==>|--x|x--x|-->|<--|---)(\|([^|]*)\|)?/g;

function parseEdgeStatement(
  stmt: string,
  ensureNode: (ref: string) => string | null,
  edges: GeneratedEdge[],
  quoted: string[]
): boolean {
  // Inline label forms (`A -- reads --> B`) rewritten to pipe form first.
  // `\s+` after `--` is required so chains (`A --> B --> C`) don't misfire.
  const normalized = stmt
    .replace(/==\s*([^=|]+?)\s*==>/g, '==>|$1|')
    .replace(/-\.\s*([^.|]+?)\s*\.->/g, '-.->|$1|')
    .replace(/--\s+([^\-=|.&]+?)\s+-->/g, '-->|$1|');

  LINK_RE.lastIndex = 0;
  const links: Array<{ op: string; label?: string }> = [];
  const operands: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = LINK_RE.exec(normalized))) {
    operands.push(normalized.slice(last, m.index));
    links.push({ op: m[1], label: m[3] ? finishLabel(m[3], quoted) || undefined : undefined });
    last = m.index + m[0].length;
  }
  operands.push(normalized.slice(last));

  if (links.length === 0) return false;

  // Each operand can fan out with `&` (A --> B & C).
  const refs = operands.map((o) =>
    o
      .split('&')
      .map((x) => x.trim())
      .filter(Boolean)
  );

  for (let i = 0; i < links.length; i++) {
    const { op, label } = links[i];
    const sources = refs[i] || [];
    const targets = refs[i + 1] || [];
    for (const sRef of sources) {
      for (const tRef of targets) {
        let source = ensureNode(sRef);
        let target = ensureNode(tRef);
        if (!source || !target) continue;
        const edge: GeneratedEdge = { source, target };
        if (label) edge.label = label;
        if (op === '-.->') edge.type = 'event';
        if (op === '<-->') edge.bidirectional = true;
        if (op === '<--') {
          // Reverse arrow: swap direction.
          edge.source = target;
          edge.target = source;
          [source, target] = [target, source];
        }
        edges.push(edge);
      }
    }
  }

  return true;
}

// --- Icon guessing ------------------------------------------------------------------

type NodeCategory = NonNullable<GeneratedNode['category']>;

interface IconGuess {
  icon: string;
  category?: NodeCategory;
}

const CATEGORY_KEYWORDS: Array<[NodeCategory, RegExp]> = [
  ['database', /\b(db|database|postgres\w*|postgre\w*|mysql|maria\w*|mongo\w*|redis|sql|cassandra|dynamo\w*|elastic\w*|sqlite|supabase)\b/],
  ['cloud', /\b(aws|gcp|azure|google cloud|cloudflare|vercel|netlify|heroku|digitalocean)\b/],
  ['network', /\b(gateway|lb|load\s*balancer|proxy|router|cdn|dns|ingress|nginx|traefik|api\s*gateway)\b/],
  ['security', /\b(auth|oauth|vault|iam|waf|firewall|sso|okta|auth0|cognito|keycloak)\b/],
  ['monitoring', /\b(grafana|prometheus|metrics|monitoring|logging|logs|datadog|sentry|observability|tracing)\b/],
  ['storage', /\b(s3|bucket|storage|disk|volume|blob|gcs|minio|efs)\b/],
  ['devops', /\b(docker|k8s|kubernetes|ci|cd|terraform|jenkins|helm|ansible|github actions|gitlab)\b/],
  ['frontend', /\b(frontend|front[- ]?end|web\s*app|spa|browser|client|ui|mobile|ios|android)\b/],
  ['backend', /\b(api|server|backend|back[- ]?end|service|worker|microservice|lambda|function)\b/],
];

/**
 * Deterministic label → icon lookup against the curated POPULAR_ICONS list
 * (which is known to exist; `isValidIconId` is syntactic only, so it can't be
 * used as an existence oracle). Falls back to a category icon, then si:files.
 */
function guessIcon(label: string): IconGuess {
  const norm = label.toLowerCase().trim();
  const compact = norm.replace(/[^a-z0-9]/g, '');
  if (!compact) return { icon: 'si:files' };

  const compactOf = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const toGuess = (entry: (typeof POPULAR_ICONS)[number]): IconGuess => ({
    icon: entry.icon,
    category: toNodeCategory(entry.category),
  });

  // 1. Exact title match ("PostgreSQL", "Node.js")
  for (const entry of POPULAR_ICONS) {
    if (entry.title.toLowerCase() === norm) return toGuess(entry);
  }

  // 2. Compact equality with title or icon name ("postgresql", "nodedotjs")
  for (const entry of POPULAR_ICONS) {
    const iconName = entry.icon.split(':')[1] ?? '';
    if (compact === compactOf(entry.title) || (iconName && compact === compactOf(iconName))) {
      return toGuess(entry);
    }
  }

  // 3. Word matching, longest word first — brands lead the label, so this
  // keeps "React Web App" on React instead of the generic "Web App" entry
  // (which wins plain longest-containment). Dotted words match dotted titles
  // ("Node.js API" → Node.js); icon-name prefix/suffix catches
  // "Postgres" → postgresql and "Kafka" → apachekafka.
  const words = norm
    .split(/\s+/)
    .map((w) => w.replace(/^[^a-z0-9]+|[^a-z0-9.]+$/g, ''))
    .filter((w) => w.replace(/[^a-z0-9]/g, '').length >= 3)
    .sort((a, b) => b.length - a.length);
  for (const word of words) {
    const wCompact = compactOf(word);
    for (const entry of POPULAR_ICONS) {
      const iconName = (entry.icon.split(':')[1] ?? '').toLowerCase();
      if (
        word === entry.title.toLowerCase() ||
        (iconName && word === iconName) ||
        wCompact === compactOf(entry.title) ||
        (iconName && wCompact === compactOf(iconName)) ||
        (iconName && wCompact.length >= 5 &&
          (compactOf(iconName).startsWith(wCompact) || compactOf(iconName).endsWith(wCompact)))
      ) {
        return toGuess(entry);
      }
    }
  }

  // 4. Containment, longest match wins ("Postgres DB" contains "postgres")
  let best: { entry: (typeof POPULAR_ICONS)[number]; length: number } | null = null;
  for (const entry of POPULAR_ICONS) {
    const candidates = [compactOf(entry.title), compactOf(entry.icon.split(':')[1] ?? '')];
    for (const c of candidates) {
      if (c.length >= 3 && compact.includes(c) && (!best || c.length > best.length)) {
        best = { entry, length: c.length };
      }
    }
  }
  if (best) return toGuess(best.entry);

  // 5. Keyword → category fallback ("User Database" → database icon)
  for (const [category, re] of CATEGORY_KEYWORDS) {
    if (re.test(norm)) {
      return { icon: getFallbackIconForCategory(category), category };
    }
  }

  return { icon: 'si:files' };
}

/** IconCategory has 'all'/'brands' which GeneratedNode.category doesn't allow. */
function toNodeCategory(category?: string): NodeCategory | undefined {
  if (!category || category === 'all' || category === 'brands') return undefined;
  return category as NodeCategory;
}

function truncate(s: string, max = 60): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}
